export interface DataModelField {
  name: string;
  type: string;
  description: string;
}

export interface DataModel {
  id: string;
  name: string;
  description: string;
  /** Endpoint ids (from apiEndpoints.ts) that produce or consume this shape. */
  usedByEndpoints: string[];
  fields: DataModelField[];
  example: string;
}

export const dataModels: DataModel[] = [
  {
    id: 'project',
    name: 'Project',
    description: 'A physical site. The top level of the Project → Layout → Configuration hierarchy. Created implicitly by Validate File (API Demo) or selected from existing data (Report Demo).',
    usedByEndpoints: ['validate-file', 'my-projects', 'list-projects', 'get-project', 'legacy-layout-configurations'],
    fields: [
      { name: 'id', type: 'integer', description: 'Primary key' },
      { name: 'uuid', type: 'string', description: 'Internal UUID' },
      { name: 'project_id', type: 'string | null', description: 'External reference ID, if imported from another system' },
      { name: 'project_name', type: 'string', description: 'Display name' },
      { name: 'owner_uuid', type: 'string', description: 'Cognito UUID of the owning user — all list/detail queries are scoped to this' },
      { name: 'owner_email_id', type: 'string', description: 'Owning user\'s email' },
      { name: 'latitude / longitude', type: 'number', description: 'Site coordinates (decimal degrees)' },
      { name: 'city / state', type: 'string | null', description: 'Reverse-geocoded or user-supplied location' },
      { name: 'epsg_code', type: 'integer | null', description: 'Coordinate reference system EPSG code' },
      { name: 'epsg_from_input', type: 'boolean', description: 'True if the EPSG code came from the uploaded file rather than being looked up' },
      { name: 'elevation_source', type: 'string', description: 'e.g. "USGS" — dataset used for elevation, when satellite elevation was used' },
      { name: 'elevation_resolution', type: 'number', description: 'Elevation dataset pixel size' },
      { name: 'layouts_count / layouts', type: 'integer | Layout[]', description: 'Count in list views; full nested Layout objects in detail views' },
      { name: 'created_date', type: 'ISO 8601 string', description: 'Creation timestamp' }
    ],
    example: `{
  "id": 5862,
  "uuid": "abc-123",
  "project_name": "Salt_river",
  "owner_uuid": "cognito-uuid",
  "owner_email_id": "user@example.com",
  "latitude": 37.57,
  "longitude": -5.15,
  "city": "Seville",
  "state": "Andalusia",
  "epsg_code": 25830,
  "epsg_from_input": true,
  "layouts_count": 3,
  "created_date": "2026-06-17T05:45:00.000000+00:00"
}`
  },

  {
    id: 'layout',
    name: 'Layout',
    description: 'A single site plan/drawing within a Project — one uploaded file (DXF/CSV/XLSX/PVC) or one DP-derived layout. Holds the pitch and unit system shared by every Configuration under it.',
    usedByEndpoints: ['validate-file', 'list-layouts', 'get-layout', 'list-runs', 'legacy-layout-configurations'],
    fields: [
      { name: 'id', type: 'integer', description: 'Primary key' },
      { name: 'uuid', type: 'string', description: 'Internal UUID' },
      { name: 'name', type: 'string', description: 'Display name' },
      { name: 'source', type: 'string', description: '"DXF" | "CSV" | "XLSX" | "PVC" | "DP" | "DP-Layout" — determines which fields/flows apply (e.g. elevation availability, wind-exposure summary availability)' },
      { name: 'source_unit', type: '"SI" | "Imperial"', description: 'Unit system used for all storage values on this layout\'s configurations' },
      { name: 'terrainpro_project_id', type: 'integer', description: 'FK to Project' },
      { name: 'pitch', type: 'number', description: 'Row pitch; must be non-zero before Create Map Layers will succeed' },
      { name: 'is_archived / is_deleted', type: 'boolean', description: 'Soft-delete/archive flags — deleted layouts are excluded from List Layouts' },
      { name: 'configurations_count / configurations', type: 'integer | Configuration[]', description: 'Count in list views; full nested Configuration objects in detail views' },
      { name: 'created_date', type: 'ISO 8601 string', description: 'Creation timestamp' }
    ],
    example: `{
  "id": 456,
  "uuid": "def-456",
  "name": "Layout A",
  "source": "DXF",
  "source_unit": "SI",
  "terrainpro_project_id": 5862,
  "pitch": 5.5,
  "is_archived": false,
  "is_deleted": false,
  "created_date": "2026-06-17T05:50:00.000000+00:00"
}`
  },

  {
    id: 'configuration',
    name: 'Configuration',
    description: 'One parameter set (min pile reveal, slope limits, reveal window, articulation angle, algorithm version, optimization goal) plus the Jobs it has queued/run. This is the unit that Run / Outputs / Layers / Pile Summary all operate on.',
    usedByEndpoints: ['create-configuration', 'run-configuration', 'single-run', 'batch-run', 'get-configuration', 'list-configurations', 'legacy-layout-configurations'],
    fields: [
      { name: 'id / config_id', type: 'integer', description: 'Primary key (field is named "id" in REST responses, "config_id" in run-endpoint responses)' },
      { name: 'configuration_name / config_name', type: 'string', description: 'Display name' },
      { name: 'terrain_pro_layout_id', type: 'integer', description: 'FK to Layout' },
      { name: 'min_pile_reveal', type: 'number', description: 'Minimum pile reveal height constraint' },
      { name: 'max_tt_axis_slope_north / _south', type: 'number', description: 'Max torque-tube axis slope in each direction (max_slope_north/max_slope_south in request bodies)' },
      { name: 'reveal_window', type: 'number', description: 'Stored in meters/feet; entered/displayed in mm/inches (display unit given by reveal_window_display_unit on run responses)' },
      { name: 'max_slope_change', type: 'number', description: 'Stored name for max_articulation_angle' },
      { name: 'algorithm_version', type: 'number', description: 'e.g. 6.0 (backend default) or 6.3 (recommended, used by both demos)' },
      { name: 'optimization_goal', type: '"earthwork" | "pileReveal"', description: 'Grading objective; defaults to "earthwork" if omitted, validated server-side' },
      { name: 'status', type: 'string', description: '"draft" | "running" | "Completed" | error states — casing is inconsistent between the clean-API and legacy responses' },
      { name: 'is_active / is_bookmarked / is_completed', type: 'boolean', description: 'Flags on the detail serializer' },
      { name: 'user_uuid / email_id', type: 'string', description: 'User who created/ran this configuration' },
      { name: 'run_start_time / run_end_time / duration', type: 'ISO 8601 string / string', description: 'Populated once a run has executed' },
      { name: 'jobs / jobs_count', type: 'Job[] | integer', description: 'Nested Jobs in detail views; count in list views' },
      { name: 'created_date', type: 'ISO 8601 string', description: 'Creation timestamp' }
    ],
    example: `{
  "id": 789,
  "configuration_name": "Configuration 1",
  "terrain_pro_layout_id": 456,
  "min_pile_reveal": 2.0,
  "max_tt_axis_slope_north": 8.5,
  "max_tt_axis_slope_south": 8.5,
  "reveal_window": 0.1,
  "max_slope_change": 0.75,
  "algorithm_version": 6.3,
  "status": "Completed",
  "is_active": true,
  "is_completed": true,
  "created_date": "2026-06-17T05:55:00.000000+00:00"
}`
  },

  {
    id: 'job',
    name: 'Job',
    description: 'One unit of async work: a validation, an extraction, or one solution stage (solution_one..four) of a run. Every long-running action returns a job_id that Get Job Status is polled with until status is terminal.',
    usedByEndpoints: ['get-job-status', 'list-jobs', 'get-job-detail', 'run-configuration', 'single-run', 'batch-run'],
    fields: [
      { name: 'job_id / id', type: 'string (UUID)', description: 'Primary key (field is "job_id" on status responses, "id" on the Jobs REST resource)' },
      { name: 'job_type', type: 'string', description: '"validation" | "extraction" | "solution_one" | "solution_two" | "solution_three" | "solution_four"' },
      { name: 'status', type: 'string', description: '"pending" | "queued" | "waiting_for_sol1" | "in_progress" | "running" | "completed" | "success" | "failed" | "error" — different endpoints use different subsets of this vocabulary, always treat "completed"/"success" as done and "failed"/"error" as terminal-failure' },
      { name: 'progress', type: 'integer (0-100)', description: 'Percent complete, where reported' },
      { name: 'file_type', type: 'string', description: '"DXF" | "CSV" | "XLSX" | "PVC"' },
      { name: 'file_path', type: 'string', description: 'S3 key of the associated input file' },
      { name: 'process_validation', type: 'boolean', description: 'Whether the job passed validation' },
      { name: 'process_result', type: 'object', description: 'Job-type-specific result payload — shape differs for validation vs. extraction vs. solution jobs' },
      { name: 'error_message', type: 'string | null', description: 'Populated when status is a failure state' },
      { name: 'configuration_id / configuration', type: 'integer', description: 'FK to Configuration' },
      { name: 'layout_id / terrain_pro_layout', type: 'integer', description: 'FK to Layout (denormalized onto the job for convenience)' },
      { name: 'project_id / terrain_pro_project', type: 'integer', description: 'FK to Project (denormalized)' },
      { name: 'job_data', type: 'object', description: 'Full input parameters + computed results, only present on Get Job Detail' },
      { name: 'webhook_url', type: 'string | null', description: 'Optional caller-supplied URL notified on completion/failure' },
      { name: 'created_at / updated_at', type: 'ISO 8601 string', description: 'Timestamps' }
    ],
    example: `{
  "job_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "job_type": "solution_one",
  "status": "completed",
  "progress": 100,
  "file_type": "CSV",
  "process_validation": true,
  "process_result": { "success": true, "piles_count": 11500 },
  "error_message": null,
  "configuration_id": 789,
  "layout_id": 456,
  "project_id": 123,
  "created_at": "2026-06-17T05:56:00.000000+00:00",
  "updated_at": "2026-06-17T06:00:00.000000+00:00"
}`
  },

  {
    id: 'validation-data',
    name: 'ValidationData (per file type)',
    description: 'The file-type-specific payload inside Get Validation Data\'s validation_data key. Shape depends entirely on file_type — this is the input the caller must inspect to build the subsequent Extract File Data request.',
    usedByEndpoints: ['validate-file', 'get-validation-data', 'extract-file'],
    fields: [
      { name: 'DXF: drawingUnit', type: 'string', description: '"Meters" | "Feet"' },
      { name: 'DXF: coordinateSystem.epsg', type: 'integer', description: 'Auto-detected EPSG code — pass through as extraction\'s epsg parameter' },
      { name: 'DXF: elevationLayer[]', type: '{color, layerName}[]', description: 'Candidate elevation layers — user selects a subset as extraction\'s layers parameter' },
      { name: 'DXF: nestedBlocks[]', type: '{width, length, rackBlockName, nestedBlocks[]}[]', description: 'Candidate rack blocks (and their nested sub-blocks) — selections become checked_blocks / checked_nested_blocks' },
      { name: 'CSV: csv_records', type: 'Record<string, string[]>', description: 'Raw preview rows keyed "Row 0", "Row 1", ... for the user to confirm header/data rows' },
      { name: 'CSV: mapping_data', type: 'Record<string, {column_index, column_description}>', description: 'Detected column mapping — pass through as-is to extraction\'s mapping_data parameter' },
      { name: 'XLSX: information_area[]', type: '{row, area_name}[]', description: 'Selectable sheet areas — chosen row numbers become extraction\'s checked_areas_rows' },
      { name: 'XLSX: pv_module_info / rack_info', type: 'object', description: 'Module/rack metadata read from the workbook' }
    ],
    example: `// DXF
{
  "drawingUnit": "Meters",
  "coordinateSystem": { "description": "WGS 84 / UTM zone 30N", "epsg": 25830 },
  "elevationLayer": [ { "color": 9, "layerName": "C-TOPO-MAJR" } ],
  "nestedBlocks": [ { "rackBlockName": "1P60_tracker", "nestedBlocks": [ { "selected": false, "nestedBlockName": "heavy pier edge" } ] } ]
}`
  },

  {
    id: 'pile-binning-analysis',
    name: 'PileBinningAnalysis (dual format)',
    description: 'The shared "dual" pile-binning response shape returned by both the clean API\'s Get Pile Summary & Rack Summary and the legacy Get Pile Binning Data. Bins pile reveal ranges by steel section type, computing both an "imported" (as-designed) and a "calculated" (as-analyzed) view side by side.',
    usedByEndpoints: ['pile-summary', 'legacy-pile-binning-data', 'legacy-pile-binning-auto-save', 'legacy-pile-binning-split-range', 'legacy-pile-binning-delete-range'],
    fields: [
      { name: 'analysis', type: '[BinsByKey, Totals, ChartData]', description: 'Fixed 3-element array: [0] = {imported_data, calculated_data} keyed by pile/section type, [1] = {imported_totals, calculated_totals}, [2] = {imported_chart_data, calculated_chart_data}' },
      { name: 'PileBin.start / end', type: 'number', description: 'Reveal range bounds for this bin, in `unit`' },
      { name: 'PileBin.count', type: 'integer', description: 'Number of piles in this bin' },
      { name: 'PileBin.percentage', type: 'number', description: 'Share of total pile count' },
      { name: 'PileBin.section_type', type: 'string', description: 'Steel section designation, e.g. "W6x12"' },
      { name: 'PileBin.minimum_embedment / pile_length / total_weight', type: 'number', description: 'Derived from Recalculate Pile Length & Weight (Legacy) when edited interactively' },
      { name: 'file_name', type: 'string', description: 'Source CSV/DXF file name the analysis was computed from' },
      { name: 'dataSource', type: 'string', description: 'Which side ("imported" or "calculated") is currently the primary display' },
      { name: 'unit', type: '"ft" | "m"', description: 'Unit system used for start/end/pile_length values' },
      { name: 'layout_name', type: 'string', description: '"{layoutName} / {configurationName}" display string' }
    ],
    example: `{
  "analysis": [
    { "imported_data": { "W6x12-A": [ { "section_type": "W6x12", "count": 120, "start": 1.2, "end": 1.8 } ] },
      "calculated_data": { "W6x12-A": [ { "section_type": "W6x12", "count": 120 } ] } },
    { "imported_totals": { "total_weight": 64200, "total_count": 535 }, "calculated_totals": { "total_weight": 64200, "total_count": 535 } },
    { "imported_chart_data": [], "calculated_chart_data": [] }
  ],
  "file_name": "config_101.csv",
  "dataSource": "calculated",
  "unit": "ft"
}`
  },

  {
    id: 'map-layer',
    name: 'MapLayer',
    description: 'One deck.gl-renderable artifact (a texture, elevation heightmap, or terrarium PNG, or the elevation manifest) generated for a completed configuration.',
    usedByEndpoints: ['create-layers', 'list-layers'],
    fields: [
      { name: 'name', type: 'string', description: 'Human-readable label, e.g. "Heatmap Fixed texture"' },
      { name: 'layer_type', type: '"texture" | "elevation" | "terrarium" | "manifest"', description: 'Artifact category' },
      { name: 'surface_mode', type: 'string | null', description: '"heatmap_fixed" | "heatmap_dynamic" | "cutfill" | "original" | "finish" | null (manifest has no surface_mode)' },
      { name: 's3_key', type: 'string', description: 'S3 object key, once known' },
      { name: 'status', type: '"pending" | "completed"', description: 'Per-layer generation status' },
      { name: 'download_url', type: 'string', description: 'Presigned S3 URL, present once status is "completed"' },
      { name: 'expires_in', type: 'integer', description: 'Presigned URL TTL in seconds' }
    ],
    example: `{
  "name": "Heatmap Fixed texture",
  "layer_type": "texture",
  "surface_mode": "heatmap_fixed",
  "s3_key": "development/project/5100/layout/8421/terrain_101_heatmap_fixed.png",
  "status": "completed",
  "download_url": "https://s3.amazonaws.com/...",
  "expires_in": 3600
}`
  },

  {
    id: 'report-request',
    name: 'ReportGenerationRequest',
    description: 'The payload shared (with minor field-naming differences) by the clean API\'s Generate Nextracker Advanced Report and the legacy Generate Advanced Report (DOCX). Bundles project/layout display metadata, the selected configurations, and revision/input-file history into one Celery job.',
    usedByEndpoints: ['legacy-generate-report-docx'],
    fields: [
      { name: 'projectName / location / coordinateSystem / epsgCode', type: 'string', description: 'Display metadata for the report cover page' },
      { name: 'latitude / longitude', type: 'string', description: 'Display-formatted (typically DMS) coordinate strings' },
      { name: 'lat / lon', type: 'number', description: 'Raw decimal coordinates — fallback used to fetch a satellite thumbnail for the location map when no screenshot is available (this API has no browser/canvas, unlike the production webapp)' },
      { name: 'filteredConfigIds', type: 'integer[]', description: 'Configuration IDs to include in the report' },
      { name: 'selectedConfigDetails', type: 'object[]', description: 'Legacy-endpoint only: enriched Configuration objects (from Get Layout Configurations (Legacy)) with earthWorkData already converted to display units' },
      { name: 'revisionDetails', type: 'Revision[]', description: '{rev, date, description, preparedBy/checkedBy/approvedBy or prepared_by/reviewed_by} rows' },
      { name: 'inputFiles', type: 'InputFile[]', description: '{filename, format, dated} or {file_type, file_name, scale, unit, datum, projection} rows' },
      { name: 'bounds', type: 'object', description: 'Legacy-endpoint only: map bounds from Get/Create Layout Tiles & GeoJSON, embedded so the report\'s map image can be rendered server-side' },
      { name: 'email', type: 'string', description: 'Always overridden server-side from the authenticated user\'s token' }
    ],
    example: `{
  "projectName": "Salt_river",
  "location": "Seville, Andalusia",
  "latitude": "37°34'12.0\\"N",
  "longitude": "5°9'0.0\\"W",
  "lat": 37.57,
  "lon": -5.15,
  "coordinateSystem": "WGS 84 / UTM zone 30N",
  "epsgCode": "25830",
  "filteredConfigIds": [789, 790],
  "revisionDetails": [ { "rev": "A", "date": "2026-06-17", "description": "Initial issue" } ],
  "inputFiles": [ { "filename": "site_survey.dxf", "format": "DXF", "dated": "2026-05-01" } ]
}`
  }
];
